<script setup lang="ts">
  const props = withDefaults(
    defineProps<{
      autoClose?: boolean
      cancelLabel?: string
      confirmLabel?: string
      disable?: boolean
      loading?: boolean
      message?: string
      width?: string
    }>(),
    {
      message: '',
      confirmLabel: 'base_yes',
      cancelLabel: 'base_no',
      loading: false,
      width: '233px',
      autoClose: true,
    },
  )
  const emits = defineEmits<{
    (e: 'confirm'): void
    (e: 'cancel'): void
  }>()
  const isOpen = defineModel<boolean>({ required: true })
  const handleConfirm = () => {
    if (props.autoClose) {
      isOpen.value = false
    }
    emits('confirm')
  }
</script>

<template>
  <QDialog v-model="isOpen">
    <QCard
      class="dialog__container"
      :style="{ '--dialog-width': width }"
      flat
    >
      <slot name="content">
        <span
          v-if="message"
          class="dialog__text"
        >
          {{ $t(message) }}
        </span>
      </slot>
      <slot name="bottom">
        <div class="dialog__buttons">
          <QBtn
            flat
            :label="$t(confirmLabel!)"
            no-caps
            class="dialog__button"
            data-qa="base-dialog__confirm"
            :disable="loading || disable"
            @click="handleConfirm"
          />
          <QBtn
            v-close-popup
            flat
            :label="$t(cancelLabel!)"
            no-caps
            class="dialog__button"
            data-qa="base-dialog__cancel"
            :disable="loading"
            @click="$emit('cancel')"
          />
        </div>
      </slot>
    </QCard>
  </QDialog>
</template>

<style scoped lang="scss">
  .dialog {
    &__container {
      padding: 49px 43px 41px;
      @include flex-container(column, center, center);
      gap: 18px;
      background-color: var(--component-base-dialog-container-background-color);
      border-radius: 20px;
      overflow: hidden;
    }
    &__text {
      @include font-montserrat-700(18px, 22px, var(--component-base-dialog-text-color));
      width: var(--dialog-width);
      text-align: center;
    }
    &__buttons {
      @include flex-container(row, center, center);
      gap: 24px;
      width: 100%;
    }
    &__button {
      width: 196px;
      height: 35px;
      border-radius: var(--radius-full);
      @include font-montserrat-500(15px, 18px);
      transition: all 0.2s ease-in-out;

      &:first-child {
        background-color: var(--component-base-dialog-confirm-button-background-color);
        color: var(--component-base-dialog-confirm-button-text-color);
        &:hover {
          background-color: var(--component-base-dialog-confirm-button-background-color-hover);
          color: var(--text-inverse) !important;
        }
      }

      &:last-child {
        background-color: var(--component-base-dialog-cancel-button-background-color);
        color: var(--component-base-dialog-cancel-button-text-color);
        &:hover {
          background-color: var(--component-base-dialog-cancel-button-background-color-hover);
          color: var(--component-base-dialog-cancel-button-text-color-hover) !important;
        }
      }
    }
  }
  @include mobile {
    .dialog {
      &__button {
        max-width: 196px;
        width: 100%;
      }
    }
  }
</style>
